import http from '../helpers/http';

const TaskBoard = (token: string) => {
  const headers = {
    'x-access-token': token,
  };

  return {
    getMainBoard: async (schoolId: string) => {
      const result = await http.get(`/task-board/school/${schoolId}`, { headers });
      return result.data;
    },
    getConcludedBoard: async (schoolId: string) => {
      const result = await http.get(`/task-board/school/${schoolId}/concluded`, { headers });
      return result.data;
    },
    moveCard: async (schoolId: string, cardId: string, body: { listId: string; index: number }) => {
      const result = await http.patch(`/task-board/school/${schoolId}/cards/${cardId}/move`, body, { headers });
      return result.data;
    },
    createCard: async (schoolId: string, body: { listId: string; title?: string }) => {
      const result = await http.post(`/task-board/school/${schoolId}/cards`, body, { headers });
      return result.data;
    },
    renameList: async (schoolId: string, listId: string, title: string) => {
      const result = await http.patch(`/task-board/school/${schoolId}/lists/${listId}`, { title }, { headers });
      return result.data;
    },
    updateListSettings: async (schoolId: string, listId: string, body: { defaultMemberUserIds: string[] }) => {
      const result = await http.patch(`/task-board/school/${schoolId}/lists/${listId}/settings`, body, { headers });
      return result.data;
    },
    deleteList: async (schoolId: string, listId: string) => {
      const result = await http.delete(`/task-board/school/${schoolId}/lists/${listId}`, { headers });
      return result.data;
    },
    createList: async (schoolId: string, body: { title: string }) => {
      const result = await http.post(`/task-board/school/${schoolId}/lists`, body, { headers });
      return result.data;
    },
    updateCard: async (
      schoolId: string,
      cardId: string,
      body: {
        title?: string;
        description?: string | null;
        dueDate?: string | null;
        labelIds?: string[];
        simpleLabels?: string[];
        memberUserIds?: string[];
        labelsData?: { text: string; color: string }[] | null;
        checklists?: {
          id?: string;
          title: string;
          items: { id?: string; text: string; isDone: boolean; position: number }[];
        }[];
      },
    ) => {
      const result = await http.patch(`/task-board/school/${schoolId}/cards/${cardId}`, body, { headers });
      return result.data;
    },
    createLabel: async (schoolId: string, body: { name?: string; color?: string }) => {
      const result = await http.post(`/task-board/school/${schoolId}/labels`, body, { headers });
      return result.data;
    },
    updateLabel: async (schoolId: string, labelId: string, body: { name?: string; color?: string }) => {
      const result = await http.patch(`/task-board/school/${schoolId}/labels/${labelId}`, body, { headers });
      return result.data;
    },
    listMemberCandidates: async (schoolId: string) => {
      const result = await http.get(`/task-board/school/${schoolId}/members/candidates`, { headers });
      return result.data;
    },
    saveMembers: async (schoolId: string, body: { members: { userId: string; role: string }[] }) => {
      const result = await http.put(`/task-board/school/${schoolId}/members`, body, { headers });
      return result.data;
    },
    listMyTeacherTickets: async (schoolId: string) => {
      const result = await http.get(`/task-board/school/${schoolId}/teacher-tickets/my`, { headers });
      return result.data?.data as any[];
    },
    createTeacherTicket: async (
      schoolId: string,
      body: {
        teacherName: string;
        date: string;
        classroomNumber: string;
        ticketType: 'supplies' | 'one_on_one' | 'labels' | 'monthly_curriculum_event' | 'request' | 'mealtime_check';
        suppliesData?: { whatNeed?: string; howMuchNeed?: string; whatFor?: string; needByDate?: string };
        oneOnOneData?: {
          notes?: string;
          meetingWith?: 'any_manager' | 'director' | 'education_director' | 'administrative_assistant';
        };
        monthlyExtrasData?: { month?: string; activities?: string };
        requestText?: string;
        mealtimeCheck?: boolean;
        dueDate?: string | null;
        labelsData?: { text: string; color: string }[] | null;
      },
    ) => {
      const result = await http.post(`/task-board/school/${schoolId}/teacher-tickets`, body, { headers });
      return result.data;
    },
    completeCard: async (schoolId: string, cardId: string) => {
      const result = await http.post(`/task-board/school/${schoolId}/cards/${cardId}/complete`, {}, { headers });
      return result.data;
    },
    restoreCard: async (schoolId: string, cardId: string, body: { listId: string }) => {
      const result = await http.post(`/task-board/school/${schoolId}/cards/${cardId}/restore`, body, { headers });
      return result.data;
    },
    uploadAttachment: async (schoolId: string, cardId: string, formData: FormData) => {
      const result = await http.post(`/task-board/school/${schoolId}/cards/${cardId}/attachments`, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      return result.data;
    },
    deleteAttachment: async (schoolId: string, cardId: string, attachmentId: string) => {
      const result = await http.delete(`/task-board/school/${schoolId}/cards/${cardId}/attachments/${attachmentId}`, {
        headers,
      });
      return result.data;
    },
    listComments: async (schoolId: string, cardId: string) => {
      const result = await http.get(`/task-board/school/${schoolId}/cards/${cardId}/comments`, { headers });
      return result.data;
    },
    createComment: async (schoolId: string, cardId: string, text: string) => {
      const result = await http.post(`/task-board/school/${schoolId}/cards/${cardId}/comments`, { text }, { headers });
      return result.data;
    },
    createCardRecurrence: async (schoolId: string, cardId: string, body: TaskBoardCardRecurrencePayload) => {
      const result = await http.post(`/task-board/school/${schoolId}/cards/${cardId}/recurrence`, body, { headers });
      return result.data;
    },
    updateCardRecurrence: async (schoolId: string, cardId: string, body: TaskBoardCardRecurrencePayload) => {
      const result = await http.patch(`/task-board/school/${schoolId}/cards/${cardId}/recurrence`, body, { headers });
      return result.data;
    },
    deleteCardRecurrence: async (schoolId: string, cardId: string) => {
      const result = await http.delete(`/task-board/school/${schoolId}/cards/${cardId}/recurrence`, { headers });
      return result.data;
    },
  };
};

export type TaskBoardCardRecurrencePayload = {
  frequency: 'daily' | 'weekly' | 'monthly';
  interval: number;
  endType: 'never' | 'on_date' | 'after_count';
  endDate?: string | null;
  maxOccurrences?: number | null;
  skipWeekends?: boolean;
};

export default TaskBoard;
